import { Fragment, FC, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { RedoxResultType } from "DataTypes/Services/Patient.type";
import Loader from "Components/Loader";
import NoData from "Components/NoData";
import { SideModalProps } from "DataTypes/Modal";
import { PatientInitialStateType } from "DataTypes/Redux";
import { useSelector } from "react-redux";
import RedoxFormatter from "Utils/Redox.Formatter";
import DateTime from "Utils/DateTime";
import UpdateResult from "./UpdateResult";
import { CommonStyle } from "Components";
import ImgPath from "Constant/Imgs";
import RedoxApi from "Service/Redox.api";

export interface DataActionType {
  data: RedoxResultType | null;
  action: "ADD" | "EDIT" | "VIEW" | "DELETE" | null;
}

const Result: FC<SideModalProps> = (props) => {
  const { onClose } = props;
  const { patientInfo }: PatientInitialStateType = useSelector(
    (state: any) => state?.patient
  );
  const [resultData, setResultData] = useState<RedoxResultType[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataAction, setDataAction] =
    useState<DataActionType>({ data: null, action: null });

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults: () => void = async () => {
    setLoading(true);
    const res = await RedoxApi.ResultSearch({ id1: patientInfo.id });
    if (res?.success) {
      setResultData(RedoxFormatter.ResultList(res?.data?.results));
    } else setResultData([]);
    setLoading(false);
  };

  const handleCloseLayer2: () => void = () => {
    setDataAction({ data: null, action: null })
  };

  const displayResultHead: (data: RedoxResultType) => React.ReactElement = (data) => {
    return (
      <Box sx={{ ...sxMedBox }}>
        <Typography variant="subtitle1" sx={{ ...sxMedName }}>
          {data.resultName} — {data.observationName}
        </Typography>
      </Box>
    );
  };

  const Layer1Header = (
    <Box sx={{ ...sxHeader, ...CommonStyle.sxRow }}>
      <Typography fontWeight="bold">Results</Typography>
      <Box display={"flex"}>
        <Box
          sx={{ ...CommonStyle.sxRoundIconsMainSm, mr: 1 }}
          onClick={() => {
            handleCloseLayer2();
            onClose();
          }}
        >
          <ImgPath.CloseIcon titleAccess="Close" />
        </Box>
      </Box>
    </Box>
  );

  const Layer1 = (
    <Fragment>
      {Layer1Header}
      <Box sx={{ ...sxMain }}>
        {loading ? (
          <Loader />
        ) : resultData.length ? (
          resultData.map((el) => {
            return (
              <Box key={el.id} sx={{ ...sxContainer }}>
                {displayResultHead(el)}
                <Typography variant="subtitle1">
                  {el.observationValue
                    ? [el.observationValue, el.observationUnit]
                      .filter(Boolean)
                      .join(" ")
                    : "—"}
                </Typography>
                <Typography variant="subtitle1">
                  Observation Date: {DateTime.ToLocalDate(el.dateTime)}
                </Typography>
                <Typography variant="subtitle1">
                  Status: {el.status || "—"}
                </Typography>
              </Box>
            );
          })
        ) : (
          <NoData />
        )}
      </Box>
    </Fragment>
  );

  return (
    <Fragment>
      <Box width="30%">{Layer1}</Box>
      <Box width="1%"></Box>
      {dataAction.action && (
        <Box width="64%">
          <UpdateResult
            dataAction={dataAction}
            onClose={handleCloseLayer2}
            refreshList={fetchResults}
          />
        </Box>
      )}
    </Fragment>
  );
};

export default Result;

const sxHeader = {
  justifyContent: "space-between",
  height: "10%",
  px: 2,
};

const sxMain = {
  height: "100%",
  overflow: "auto",
  px: 2,
};

const sxContainer = {
  py: 1,
  borderBottom: "1px solid",
  borderBottomColor: "custom.main",
};

const sxMedBox = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "space-between",
};

const sxMedName = {
  fontWeight: "bold",
  width: "80%",
  lineBreak: "anywhere",
};
